import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import * as styles from './LinkDisclaimer.module.scss';
import useInAppBrowser from '../../hooks/useInAppBrowser';
import useIsInstagramBrowser from '../../hooks/useIsInstagramBrowser';
import useExternalBrowserRedirectIntent from '../../hooks/useExternalBrowserRedirectIntent';
import LinkDisclaimerContent from './LinkDisclaimerContent';
import { getMaskedUrl } from '../../lib/url';
import { markOutboundLinkOpened } from '../../lib/outbound-link';
import { IconClose } from '../../assets/SvgSprite';

const LinkDisclaimer = ({
  url,
  creatorName,
  onClose,
  v2 = false,
  showExternalBrowserInstructions = false,
  withSensitiveLinkHoldToOpen = false,
  disableDeeplinks = false,
}) => {
  const isInstagram = useIsInstagramBrowser();

  const [didNavigationSilentBlock, setDidNavigationSilentBlock] =
    useState(false);

  const shouldUseInstagramHoldToOpen =
    withSensitiveLinkHoldToOpen && isInstagram;

  const withExternalBrowserInstructions =
    showExternalBrowserInstructions || didNavigationSilentBlock;

  const maskedUrl = useMemo(
    () =>
      getMaskedUrl(url, {
        withSensitiveLinkHoldToOpen: shouldUseInstagramHoldToOpen,
        disableDeeplinks,
        forceMask: shouldUseInstagramHoldToOpen,
      }),
    [url, shouldUseInstagramHoldToOpen, disableDeeplinks]
  );

  const handleSilentNavigationBlock = useCallback(() => {
    setDidNavigationSilentBlock(true);
  }, []);

  const { handleLinkClick } = useInAppBrowser({
    creatorName,
    preferInAppBrowser: !withExternalBrowserInstructions,
    onSilentNavigationBlock: handleSilentNavigationBlock,
  });

  useExternalBrowserRedirectIntent({
    isInstagram,
    showExternalBrowserInstructions: withExternalBrowserInstructions,
    url,
  });

  const onLinkClick = useCallback(
    (e) => {
      e.preventDefault();
      markOutboundLinkOpened(url);
      handleLinkClick(maskedUrl);
    },
    [handleLinkClick, maskedUrl, url]
  );

  const onCloseClick = useCallback(
    (e) => {
      e.stopPropagation();
      onClose?.();
    },
    [onClose]
  );

  useEffect(() => {
    setDidNavigationSilentBlock(false);
  }, [url]);

  const disclaimerClassName = v2
    ? styles.linkCardDisclaimerV2
    : styles.linkCardDisclaimerV1;

  if (isInstagram && typeof document !== 'undefined') {
    const portalContent = (
      <div className={styles.linkDisclaimerInstagram}>
        {withExternalBrowserInstructions && (
          <>
            <button
              type="button"
              className={styles.linkDisclaimerCloseIconButton}
              onClick={onCloseClick}
            >
              <IconClose size={40} />
            </button>

            <div className={styles.externalBrowserInstructionsThreeDotsTooltip}>
              Click dots to open in external browser
            </div>
          </>
        )}

        <LinkDisclaimerContent
          className={styles.linkDisclaimerInstagramContent}
          url={maskedUrl}
          onLinkClick={onLinkClick}
          onCloseClick={onCloseClick}
          isInstagram={isInstagram}
          showExternalBrowserInstructions={withExternalBrowserInstructions}
        />
      </div>
    );

    return createPortal(portalContent, document.body);
  }

  return (
    <LinkDisclaimerContent
      className={disclaimerClassName}
      url={maskedUrl}
      onLinkClick={onLinkClick}
      isInstagram={isInstagram}
    />
  );
};

export default memo(LinkDisclaimer);
